import React from 'react';

/**
 * StarRating — displays a row of 5 SVG stars.
 *
 * Props:
 *   rating       number  0–5  (supports decimals for half-stars)
 *   totalReviews number  optional — shown as "(N)" next to stars
 *   size         number  star size in px (default 14)
 *   className    string  extra wrapper classes
 */
const StarRating = ({ rating = 5, totalReviews, size = 14, className = '' }) => {
    const stars = Array.from({ length: 5 }, (_, i) => {
        const filled = i < Math.floor(rating);
        const partial = !filled && i < rating; // fractional star
        const pct = partial ? Math.round((rating - Math.floor(rating)) * 100) : 0;

        return { index: i, filled, partial, pct };
    });

    return (
        <div
            className={`flex items-center gap-0.5 ${className}`}
            title={`${rating} out of 5 stars`}
            aria-label={`Rating: ${rating} out of 5`}
        >
            {stars.map(({ index, filled, partial, pct }) => (
                <svg
                    key={index}
                    width={size}
                    height={size}
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                    className="transition-all duration-150 hover:scale-110 hover:brightness-110 flex-shrink-0"
                    style={{ cursor: 'default' }}
                >
                    {/* Gradient definition for partial fill */}
                    {partial && (
                        <defs>
                            <linearGradient id={`star-grad-${index}`} x1="0" x2="1" y1="0" y2="0">
                                <stop offset={`${pct}%`} stopColor="#FACC15" />
                                <stop offset={`${pct}%`} stopColor="#CBD5E1" />
                            </linearGradient>
                        </defs>
                    )}
                    <polygon
                        points="10,1 12.39,7.26 19,7.64 14,12.14 15.82,19 10,15.27 4.18,19 6,12.14 1,7.64 7.61,7.26"
                        fill={
                            filled
                                ? '#FACC15'
                                : partial
                                    ? `url(#star-grad-${index})`
                                    : '#CBD5E1'
                        }
                        stroke={filled || partial ? '#F59E0B' : '#94A3B8'}
                        strokeWidth="0.5"
                        strokeLinejoin="round"
                    />
                </svg>
            ))}

            {totalReviews !== undefined && (
                <span className="text-xs text-slate-400 ml-1 leading-none">
                    ({totalReviews})
                </span>
            )}
        </div>
    );
};

export default StarRating;
